import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRowModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';
import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import {
  useGetCharterInventoryManagement,
  useUpdateCharterInventoryManagement,
} from '~/hooks/apis/flights-charter/useCharterInventoryManagement';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { CharterInventoryManagementRow } from '~/services/CharterService';
import { fetchCharterInventoryManagement } from '~/services/CharterService';
import { formatDateISO } from '~/services/TimeService';

import { isAdmin } from '~/utils/adminPermission';
import { downloadCsvFile } from '~/utils/csvDownload';

import {
  INVENTORY_MANAGEMENT_CSV_HEADERS,
  INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT,
  INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT,
  INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT,
  INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT,
  buildInventoryManagementUpdatePayload,
  cabinClassLabel,
  formatInventoryDate,
  formatInventoryRoute,
  formatInventoryRouteAscii,
  formatUnallocatedSeatCount,
  getInventoryManagementCsvRows,
  parseAndValidateInventoryRow,
  seatingClassLabel,
  sortInventoryManagementRows,
  validateInventoryManagementRows,
  withComputedInventoryUsage,
} from './CharterInventoryManagementHelpers';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const CSV_EXPORT_PAGE_SIZE = 1000;

function InventoryManagementToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

export default function CharterInventoryManagementPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { block, push } = useAppNavigation();
  const { user } = useCurrentUser();
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 50 });
  const [isViewMode, setIsViewMode] = useState(true);
  const [editedRows, setEditedRows] = useState<Map<string, CharterInventoryManagementRow>>(new Map());
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const hasUnsavedChanges = editedRows.size > 0;
  const canEditInventory = isAdmin(user);
  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  const queryParams = useMemo(
    () => ({
      departingDepAirport: INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT,
      departingArrAirport: INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT,
      returningDepAirport: INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT,
      returningArrAirport: INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT,
      offset,
      limit,
    }),
    [limit, offset],
  );

  const inventoryQuery = useGetCharterInventoryManagement(queryParams);
  const updateMutation = useUpdateCharterInventoryManagement();

  useEffect(() => {
    if (!canEditInventory && !isViewMode) {
      setEditedRows(new Map());
      setCellModesModel({});
      setIsViewMode(true);
    }
  }, [canEditInventory, isViewMode]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      unblockRef.current = block((location) => {
        setPendingNavigation(location);
        setIsDiscardDialogOpen(true);
        return false;
      });
    } else if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [block, hasUnsavedChanges]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const baseRows = useMemo(() => inventoryQuery.data?.result ?? [], [inventoryQuery.data?.result]);

  const baseRowMap = useMemo(() => {
    const map = new Map<string, CharterInventoryManagementRow>();
    for (const row of baseRows) {
      map.set(row.id, row);
    }
    return map;
  }, [baseRows]);

  const rows = useMemo(() => {
    return sortInventoryManagementRows(
      withComputedInventoryUsage(baseRows.map((baseRow) => editedRows.get(baseRow.id) ?? baseRow)),
    );
  }, [baseRows, editedRows]);

  const validationErrors = useMemo(() => validateInventoryManagementRows(rows), [rows]);

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<CharterInventoryManagementRow>) => {
      const validatedRow = parseAndValidateInventoryRow(newRow);
      const original = baseRowMap.get(validatedRow.id);

      setEditedRows((current) => {
        const next = new Map(current);
        if (original && validatedRow.allocation === original.allocation) {
          next.delete(validatedRow.id);
        } else {
          next.set(validatedRow.id, validatedRow);
        }
        return next;
      });

      return validatedRow;
    },
    [baseRowMap],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      if (hasUnsavedChanges) {
        enqueueSnackbar('Save or discard inventory changes before changing pages.', {
          variant: 'warning',
        });
        return;
      }

      setPaginationModel(nextPaginationModel);
    },
    [enqueueSnackbar, hasUnsavedChanges, setPaginationModel],
  );

  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (isViewMode || !params.isEditable) {
        return;
      }

      if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
        return;
      }

      setCellModesModel((prevModel) => ({
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }));
    },
    [isViewMode],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<CharterInventoryManagementRow>) => {
      if (params.field !== 'allocation') {
        return '';
      }
      const original = baseRowMap.get(params.row.id);
      return original && params.row.allocation !== original.allocation ? 'cell-modified' : '';
    },
    [baseRowMap],
  );

  const handleConfirmDiscard = useCallback(() => {
    setEditedRows(new Map());
    setCellModesModel({});
    setIsDiscardDialogOpen(false);

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    if (pendingNavigation) {
      const targetPath = pendingNavigation.pathname + pendingNavigation.search + pendingNavigation.hash;
      setPendingNavigation(null);
      push(targetPath);
    } else {
      setIsViewMode(true);
    }
  }, [pendingNavigation, push]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingNavigation(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!canEditInventory) {
      enqueueSnackbar('Only admin users can edit inventory allocations.', { variant: 'error' });
      return;
    }

    if (updateMutation.isPending) {
      return;
    }

    if (editedRows.size === 0) {
      enqueueSnackbar('No changes to save.', { variant: 'warning' });
      return;
    }

    if (validationErrors.length > 0) {
      enqueueSnackbar(validationErrors[0], { variant: 'error' });
      return;
    }

    try {
      await updateMutation.mutateAsync(buildInventoryManagementUpdatePayload(editedRows));
      enqueueSnackbar(`Inventory allocations saved (${editedRows.size} rows updated).`, { variant: 'success' });
      setEditedRows(new Map());
      setCellModesModel({});
      setIsViewMode(true);
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to save inventory allocations.', {
        variant: 'error',
      });
    }
  }, [canEditInventory, editedRows, enqueueSnackbar, updateMutation, validationErrors]);

  const handleDownloadCsv = useCallback(async () => {
    if (isExportingCsv) {
      return;
    }

    if (hasUnsavedChanges) {
      enqueueSnackbar('Save or discard inventory changes before exporting CSV.', {
        variant: 'warning',
      });
      return;
    }

    setIsExportingCsv(true);

    try {
      const allRows: Array<CharterInventoryManagementRow> = [];
      let offset = 0;
      let totalItems: number | null = null;

      do {
        const response = await fetchCharterInventoryManagement({
          departingDepAirport: INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT,
          departingArrAirport: INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT,
          returningDepAirport: INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT,
          returningArrAirport: INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT,
          offset,
          limit: CSV_EXPORT_PAGE_SIZE,
        });

        allRows.push(...response.result);
        totalItems = response.pagination.total_items;
        offset += response.pagination.limit;

        if (response.result.length === 0) {
          break;
        }
      } while (totalItems === null || offset < totalItems);

      if (allRows.length === 0) {
        enqueueSnackbar('No inventory rows available to export.', { variant: 'warning' });
        return;
      }

      const exportRows = sortInventoryManagementRows(withComputedInventoryUsage(allRows));
      downloadCsvFile(
        getInventoryManagementCsvRows(exportRows),
        INVENTORY_MANAGEMENT_CSV_HEADERS,
        `charter-inventory-management-${formatDateISO()}.csv`,
      );
      enqueueSnackbar(`CSV exported successfully with ${exportRows.length} rows.`, {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to export inventory CSV.', {
        variant: 'error',
      });
    } finally {
      setIsExportingCsv(false);
    }
  }, [enqueueSnackbar, hasUnsavedChanges, isExportingCsv]);

  const columns = useMemo<Array<GridColDef<CharterInventoryManagementRow>>>(
    () => [
      {
        field: 'depDate',
        headerName: 'Departure Date',
        flex: 1,
        minWidth: 150,
        valueFormatter: (value) => formatInventoryDate(String(value)),
      },
      {
        field: 'direction',
        headerName: 'Direction',
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_value, row) => formatInventoryRouteAscii(row),
        renderCell: (params) => formatInventoryRoute(params.row),
      },
      {
        field: 'cabinClass',
        headerName: 'Cabin class',
        flex: 1.4,
        minWidth: 190,
        valueGetter: (_value, row) => cabinClassLabel[row.cabinClass],
      },
      {
        field: 'allocation',
        headerName: 'Allocation',
        flex: 0.8,
        minWidth: 125,
        type: 'number',
        editable: true,
        rowSpanValueGetter: (_value, row) => row.id,
      },
      {
        field: 'booked',
        headerName: 'Booked',
        flex: 0.7,
        minWidth: 105,
        type: 'number',
      },
      {
        field: 'availableSeats',
        headerName: 'Available',
        flex: 0.8,
        minWidth: 115,
        type: 'number',
      },
      {
        field: 'seatingClass',
        headerName: 'Seating Class',
        flex: 1.2,
        minWidth: 190,
        valueGetter: (_value, row) => seatingClassLabel[row.seatingClass],
        rowSpanValueGetter: (_value, row) => `${row.flightInstanceId}-${row.seatingClass}`,
      },
      {
        field: 'seatingClassCapacity',
        headerName: 'Class Capacity',
        renderHeader: () => (
          <Box sx={{ lineHeight: 1.2 }}>
            <Typography variant="body2" fontWeight={500}>
              Class Capacity
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (allocated/total)
            </Typography>
          </Box>
        ),
        flex: 1.2,
        minWidth: 240,
        renderCell: (params) => (
          <Box sx={{ lineHeight: 1.25, overflow: 'hidden' }}>
            <Typography variant="body2">
              {params.row.seatingClassUsage}/{params.row.seatingClassCapacity}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatUnallocatedSeatCount(params.row)}
            </Typography>
          </Box>
        ),
        rowSpanValueGetter: (_value, row) => `${row.flightInstanceId}-${row.seatingClass}`,
      },
    ],
    [],
  );

  const hasData = !inventoryQuery.isLoading && !inventoryQuery.isError;

  return (
    <Container sx={hasData ? { width: 'fit-content' } : undefined}>
      <PageHeader title="Inventory management" />

      {inventoryQuery.isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading inventory...
          </Typography>
        </Box>
      ) : null}

      {inventoryQuery.isError ? (
        <Box mt={4}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" variant="outlined" onClick={() => inventoryQuery.refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to load inventory</AlertTitle>
            {inventoryQuery.error instanceof Error ? inventoryQuery.error.message : 'An unexpected error occurred'}
          </Alert>
        </Box>
      ) : null}

      {hasData ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} gap={2}>
            <Stack direction="row" spacing={2}>
              <LoadingButton
                variant="outlined"
                onClick={handleDownloadCsv}
                loading={isExportingCsv}
                disabled={isExportingCsv}
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </LoadingButton>
              {canEditInventory ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (isViewMode) {
                        setIsViewMode(false);
                      } else if (hasUnsavedChanges) {
                        setIsDiscardDialogOpen(true);
                      } else {
                        setIsViewMode(true);
                      }
                    }}
                  >
                    {isViewMode ? 'Edit Inventory' : 'View Mode'}
                  </Button>
                  {!isViewMode ? (
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      loading={updateMutation.isPending}
                      disabled={updateMutation.isPending || validationErrors.length > 0}
                    >
                      Save Changes
                    </LoadingButton>
                  ) : null}
                </>
              ) : null}
            </Stack>
            {validationErrors.length > 0 ? (
              <Alert severity="error" sx={{ maxWidth: 720 }}>
                {validationErrors[0]}
              </Alert>
            ) : null}
          </Stack>

          <Box sx={{ width: '100%' }}>
            <StyledDataGrid
              rows={rows}
              columns={columns}
              unstable_rowSpanning
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              rowCount={inventoryQuery.data?.pagination.total_items ?? 0}
              editMode="cell"
              isCellEditable={(params) => canEditInventory && !isViewMode && params.field === 'allocation'}
              cellModesModel={cellModesModel}
              onCellModesModelChange={setCellModesModel}
              onCellClick={handleCellClick}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              getCellClassName={getCellClassName}
              slots={{ toolbar: InventoryManagementToolbar }}
              sx={{
                maxHeight: '1000px',
                '& .cell-modified': {
                  backgroundColor: 'warning.light',
                },
                ...(!isViewMode && {
                  '& .MuiDataGrid-cell[data-field="allocation"]': {
                    outline: '1px solid',
                    outlineColor: 'grey.400',
                    outlineOffset: -1,
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      outlineColor: 'primary.main',
                    },
                  },
                  '& .MuiDataGrid-cell--editing': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                    boxShadow: 1,
                    backgroundColor: 'background.paper',
                  },
                }),
              }}
              disableRowSelectionOnClick
            />
          </Box>
        </>
      ) : null}

      <Dialog open={isDiscardDialogOpen} onClose={handleCancelDiscard}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved inventory changes.{' '}
            {pendingNavigation
              ? 'If you leave this page, they will be discarded.'
              : 'If you leave Edit Mode, they will be discarded.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={handleCancelDiscard} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
